// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Outlook calendar state — connection, current view (day/week/month/
// list), anchor date navigation, and events for the visible window.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import type {KonvoEventSeriesType, KonvoRecurrence} from '@app/features/konvo_calendar/utils/KonvoRecurrence';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, runInAction} from 'mobx';

export type KonvoCalendarView = 'day' | 'week' | 'month' | 'list';

export interface KonvoCalendarEvent {
	id: string;
	subject: string;
	start: string | null;
	end: string | null;
	is_all_day: boolean;
	is_cancelled: boolean;
	teams_join_url: string | null;
	location: string | null;
	organizer: string | null;
	web_link: string | null;
	preview: string | null;
	konvo_join_url: string | null;
	calendar_id: string | null;
	/** KONVO: series info from Graph; occurrences come expanded from calendarView. */
	type?: KonvoEventSeriesType | null;
	series_master_id?: string | null;
	show_as?: string;
	response?: string;
}

/** Graph's attendee reply; older API builds leave it out. */
export type KonvoAttendeeResponse =
	| 'none'
	| 'organizer'
	| 'tentativelyAccepted'
	| 'accepted'
	| 'declined'
	| 'notResponded';

export interface KonvoCalendarAttendee {
	name: string | null;
	address: string;
	type: string;
	response?: KonvoAttendeeResponse;
}

/** Everything the edit form needs — the list payload omits attendees and the body. */
export interface KonvoCalendarEventDetail {
	id: string;
	subject: string;
	start: string | null;
	end: string | null;
	is_all_day: boolean;
	is_cancelled: boolean;
	is_organizer: boolean;
	teams_join_url: string | null;
	location: string | null;
	organizer: string | null;
	web_link: string | null;
	konvo_join_url: string | null;
	body_text: string;
	attendees: Array<KonvoCalendarAttendee>;
	type?: KonvoEventSeriesType | null;
	series_master_id?: string | null;
	recurrence?: KonvoRecurrence | null;
}

interface EventsResponse {
	connected: boolean;
	events: Array<KonvoCalendarEvent>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const VIEW_STORAGE_KEY = 'konvoCalendarView';
const VALID_VIEWS: ReadonlyArray<KonvoCalendarView> = ['day', 'week', 'month', 'list'];

function readStoredView(): KonvoCalendarView | null {
	try {
		const raw = getProtectedLocalStorage()?.getItem(VIEW_STORAGE_KEY);
		return raw && VALID_VIEWS.includes(raw as KonvoCalendarView) ? (raw as KonvoCalendarView) : null;
	} catch {
		return null;
	}
}

export function startOfDay(date: Date): Date {
	const copy = new Date(date);
	copy.setHours(0, 0, 0, 0);
	return copy;
}

export function startOfWeek(date: Date): Date {
	const day = startOfDay(date);
	const weekday =
		KonvoCalendarPrefs.weekStart === 'sunday'
			? day.getDay() // Sunday = 0
			: (day.getDay() + 6) % 7; // Monday = 0
	return new Date(day.getTime() - weekday * DAY_MS);
}

export function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

class KonvoCalendarStore {
	connected: boolean | null = null;
	events: Array<KonvoCalendarEvent> = [];
	loading = false;
	error: string | null = null;
	view: KonvoCalendarView = readStoredView() ?? 'list';
	anchor: Date = new Date();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get timezone(): string {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
		} catch {
			return 'UTC';
		}
	}

	/** [start, end) of the window the current view needs. */
	get window(): {start: Date; end: Date} {
		const anchor = this.anchor;
		switch (this.view) {
			case 'day': {
				const start = startOfDay(anchor);
				return {start, end: new Date(start.getTime() + DAY_MS)};
			}
			case 'week': {
				const start = startOfWeek(anchor);
				return {start, end: new Date(start.getTime() + 7 * DAY_MS)};
			}
			case 'month': {
				const gridStart = startOfWeek(startOfMonth(anchor));
				return {start: gridStart, end: new Date(gridStart.getTime() + 42 * DAY_MS)};
			}
			default: {
				const start = startOfDay(new Date());
				return {start, end: new Date(start.getTime() + 14 * DAY_MS)};
			}
		}
	}

	setView(view: KonvoCalendarView): void {
		this.view = view;
		try {
			getProtectedLocalStorage()?.setItem(VIEW_STORAGE_KEY, view);
		} catch {
			// storage unavailable — the view just won't persist
		}
		void this.refresh();
	}

	goToDate(date: Date): void {
		this.anchor = date;
		this.view = 'day';
		void this.refresh();
	}

	/** Pick a day without changing the view (the phone's week strip); reloads only when the window moves. */
	selectDate(date: Date): void {
		const before = this.window.start.getTime();
		this.anchor = date;
		if (this.window.start.getTime() !== before) void this.refresh();
	}

	goToday(): void {
		this.anchor = new Date();
		void this.refresh();
	}

	step(direction: 1 | -1): void {
		const anchor = new Date(this.anchor);
		if (this.view === 'day') anchor.setDate(anchor.getDate() + direction);
		else if (this.view === 'week' || this.view === 'list') anchor.setDate(anchor.getDate() + 7 * direction);
		else anchor.setMonth(anchor.getMonth() + direction);
		this.anchor = anchor;
		void this.refresh();
	}

	/** Full detail for one event; null when it could not be loaded. */
	async fetchEvent(eventId: string): Promise<KonvoCalendarEventDetail | null> {
		try {
			const params = new URLSearchParams({timezone: this.timezone});
			const response = await http.get<KonvoCalendarEventDetail>(
				`${Endpoints.KONVO_CALENDAR_EVENT(eventId)}?${params.toString()}`,
			);
			return response.body ?? null;
		} catch {
			return null;
		}
	}

	async refresh(): Promise<void> {
		this.loading = true;
		this.error = null;
		const {start, end} = this.window;
		try {
			const params = new URLSearchParams({
				start: start.toISOString(),
				end: end.toISOString(),
				timezone: this.timezone,
			});
			const calendarIds = KonvoCalendarPrefs.calendarIds;
			if (calendarIds && calendarIds.length > 0) {
				params.set('calendars', calendarIds.join(','));
			}
			const response = await http.get<EventsResponse>(
				`${Endpoints.KONVO_CALENDAR_EVENTS}?${params.toString()}`,
				RETRY_RATE_LIMITS,
			);
			runInAction(() => {
				this.connected = response.body?.connected ?? false;
				this.events = (response.body?.events ?? []).filter((event) => !event.is_cancelled);
			});
			KonvoConnections.setMicrosoftConnected(this.connected === true);
		} catch {
			runInAction(() => {
				this.error = 'load_failed';
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}
}

export default new KonvoCalendarStore();
